/**
 * 📋 KanbanBoard — Shared drag-and-drop Kanban engine
 *
 * Reusable across modules: Ações, Problemas, future modules.
 * Provides: drag-drop, column limits, status transitions, empty states.
 */

import React, { useState, useCallback } from 'react';
import { Plus, Ghost } from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────

export interface KanbanColumnConfig {
  /** Unique column identifier (maps to status value) */
  id: string;
  /** Display title */
  title: string;
  /** Icon component */
  icon: React.FC<{ size?: number; className?: string }>;
  /** Top border color class (e.g., 'border-t-blue-600') */
  headerBorder: string;
  /** Badge color classes */
  badgeColor: string;
  /** Optional WIP limit */
  wipLimit?: number;
}

export interface KanbanCardData {
  id: string;
  status: string;
  [key: string]: unknown;
}

export interface KanbanBoardProps<T extends KanbanCardData> {
  /** Column definitions in display order */
  columns: KanbanColumnConfig[];
  /** All items to display */
  items: T[];
  /** Called when an item is dropped on a new column */
  onStatusChange: (itemId: string, newStatus: string) => void;
  /** Render a single card */
  renderCard: (item: T, isDragging: boolean) => React.ReactNode;
  /** Optional: called when "+" button is clicked on a column */
  onAddItem?: (status: string) => void;
  /** Map item status to column id (default: identity) */
  statusToColumnId?: (status: string) => string;
  /** Optional className for the board container */
  className?: string;
  /** Called when a card is clicked */
  onCardClick?: (itemId: string) => void;
  /** Full-width grid mode (columns fill available space) */
  fullWidth?: boolean;
  /** Validate if a drop is allowed (return false to reject) */
  canDrop?: (itemId: string, fromStatus: string, toStatus: string) => boolean;
}

// ─── KanbanBoard ───────────────────────────────────────────

export function KanbanBoard<T extends KanbanCardData>({
  columns,
  items,
  onStatusChange,
  renderCard,
  onAddItem,
  statusToColumnId,
  className = '',
  onCardClick,
  fullWidth = false,
  canDrop,
}: KanbanBoardProps<T>) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

  const resolveColumnId = useCallback(
    (status: string) => (statusToColumnId ? statusToColumnId(status) : status),
    [statusToColumnId],
  );

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('kanban-item-id', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverColumnId(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnId(columnId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumnId(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, columnId: string) => {
      e.preventDefault();
      const itemId = e.dataTransfer.getData('kanban-item-id');
      if (itemId) {
        const item = items.find((i) => i.id === itemId);
        const fromStatus = item ? resolveColumnId(item.status) : '';
        if (canDrop && !canDrop(itemId, fromStatus, columnId)) {
          setDraggedId(null);
          setDragOverColumnId(null);
          return;
        }
        onStatusChange(itemId, columnId);
      }
      setDraggedId(null);
      setDragOverColumnId(null);
    },
    [onStatusChange, canDrop, items, resolveColumnId],
  );

  return (
    <div className={`kanban-board h-full pb-2 ${fullWidth ? 'grid gap-3' : 'flex gap-3 overflow-x-auto'} ${className}`.trim()}
      style={fullWidth ? { gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` } : undefined}
    >
      {columns.map((column) => {
        const columnItems = items.filter((item) => resolveColumnId(item.status) === column.id);
        const isDragOver = dragOverColumnId === column.id;
        const isOverLimit = column.wipLimit != null && columnItems.length > column.wipLimit;

        return (
          <div
            key={column.id}
            className={`kanban-column flex flex-col rounded border transition-colors
              ${fullWidth ? 'min-w-0' : 'w-64 shrink-0'}
              ${isDragOver
                ? 'border-blue-400/60 bg-blue-50/30 dark:border-blue-500/40 dark:bg-blue-900/10'
                : 'border-slate-200 bg-slate-50/80 dark:border-slate-700/60 dark:bg-slate-800/30'}
            `}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Column Header */}
            <div className={`flex shrink-0 items-center justify-between border-t-[3px] px-3 py-2 ${column.headerBorder}`}>
              <div className="flex items-center gap-2">
                <column.icon size={13} className="text-slate-500 dark:text-slate-400" />
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  {column.title}
                </h3>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${column.badgeColor}`}>
                  {columnItems.length}
                </span>
                {isOverLimit && (
                  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    WIP!
                  </span>
                )}
              </div>
              {onAddItem && (
                <button
                  type="button"
                  onClick={() => onAddItem(column.id)}
                  className="rounded p-1 text-slate-400 hover:bg-slate-200/60 hover:text-slate-600 dark:hover:bg-slate-700/60 dark:hover:text-slate-300"
                  title={`Adicionar em ${column.title}`}
                >
                  <Plus size={14} />
                </button>
              )}
            </div>

            {/* Cards Container */}
            <div className="kanban-cards flex-1 space-y-2 overflow-y-auto p-2">
              {columnItems.length === 0 ? (
                <div className="flex h-20 flex-col items-center justify-center rounded border border-dashed border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500">
                  <Ghost size={20} className="mb-1 opacity-50" />
                  <span className="text-[10px] font-medium">Vazio</span>
                </div>
              ) : (
                columnItems.map((item) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item.id)}
                    onDragEnd={handleDragEnd}
                    onClick={onCardClick ? () => onCardClick(item.id) : undefined}
                    className={`transition-opacity ${onCardClick ? 'cursor-pointer' : ''} ${draggedId === item.id ? 'opacity-30' : 'opacity-100'}`}
                  >
                    {renderCard(item, draggedId === item.id)}
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default KanbanBoard;
